import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sparkles, Loader2, Download, ImagePlus, X, Send } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";

const STYLE_PRESETS = [
  { value: "vibrant", label: "Vibrant & Bold" },
  { value: "minimalist", label: "Minimalist" },
  { value: "vintage", label: "Vintage Pinoy" },
  { value: "luxurious", label: "Luxurious" },
  { value: "playful", label: "Playful Festive" },
];

const MAX_REFS = 5;

/**
 * Reusable AI Designer body. Same UI for /ai-designer (customer) and
 * /admin/ai-designer (admin). The only difference is that admin variant
 * shows extra metadata and removes the "Use for my order" CTA (already gone).
 */
const AIDesignerBody = ({ heading = "Generate your tarpaulin in seconds", subheading }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [style, setStyle] = useState("vibrant");
  const [refs, setRefs] = useState([]); // [{file, name}]
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const [lrId, setLrId] = useState("");
  const [attaching, setAttaching] = useState(false);

  // Prefill (used by admin "Generate AI Layout" from a layout request).
  // Also auto-downloads any `refs=url1|url2` URLs (customer attachments) and
  // injects them as reference photos so the admin doesn't need to re-upload.
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (p.get("prompt")) setPrompt(p.get("prompt"));
    if (p.get("width"))  setWidth(parseFloat(p.get("width"))  || 0);
    if (p.get("height")) setHeight(parseFloat(p.get("height")) || 0);
    if (p.get("lr_id"))  setLrId(p.get("lr_id"));
    const refsParam = p.get("refs");
    if (refsParam) {
      (async () => {
        const urls = refsParam.split("|").filter(Boolean).slice(0, MAX_REFS);
        const loaded = [];
        for (const u of urls) {
          try {
            const full = u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`;
            const res = await fetch(full, { credentials: "include" });
            if (!res.ok) continue;
            const blob = await res.blob();
            // Only image references are useful for image-conditioned AI
            if (!blob.type.startsWith("image/")) continue;
            const name = u.split("/").pop() || `customer_${loaded.length + 1}`;
            const file = new File([blob], name, { type: blob.type });
            loaded.push({ file, name });
          } catch { /* skip */ }
        }
        if (loaded.length) {
          setRefs(loaded);
          toast.success(`Loaded ${loaded.length} customer attachment${loaded.length === 1 ? "" : "s"} as reference`);
        }
      })();
    }
  }, [location.search]);

  const addFile = (file) => {
    if (!file) return;
    if (refs.length >= MAX_REFS) {
      toast.error(`You can attach up to ${MAX_REFS} photos`);
      return;
    }
    setRefs((prev) => [...prev, { file, name: "" }]);
  };
  const updateName = (idx, name) => {
    setRefs((prev) => prev.map((r, i) => i === idx ? { ...r, name } : r));
  };
  const removeRef = (idx) => {
    setRefs((prev) => prev.filter((_, i) => i !== idx));
  };

  const generate = async (e) => {
    e.preventDefault();
    if (!prompt.trim()) { toast.error("Describe your tarpaulin design first"); return; }
    if (refs.some((r) => !r.name.trim())) {
      toast.error("Please name each reference photo for clarity");
      return;
    }
    setLoading(true);
    setUnavailable(false);
    try {
      const fd = new FormData();
      fd.append("prompt", prompt);
      fd.append("width_ft", String(width));
      fd.append("height_ft", String(height));
      fd.append("style", style);
      fd.append("reference_names", refs.map((r) => r.name).join("|"));
      refs.forEach((r) => fd.append("references", r.file));
      const { data } = await api.post("/ai/generate-tarpaulin", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setResult(data);
      toast.success("Design generated!");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        toast.error("Please sign in to use AI Designer");
      } else if (status === 429 || status === 503) {
        setUnavailable(true);
        toast.error("AI service temporarily unavailable — try again later");
      } else {
        toast.error(err?.response?.data?.detail || "Generation failed. Try a different prompt.");
      }
    } finally {
      setLoading(false);
    }
  };

  const attachToLayoutRequest = async () => {
    if (!result?.ai_design_url || !lrId) return;
    setAttaching(true);
    try {
      await api.post(`/admin/layout-requests/${lrId}/attach-from-ai`, {
        ai_design_url: result.ai_design_url,
      });
      toast.success("Layout sent to customer for approval");
      navigate("/admin/layout-requests");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to attach layout");
    } finally {
      setAttaching(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pav-fade-in" data-testid="ai-designer-page">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-[#06B6D4] font-semibold inline-flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5" /> AI Designer
        </p>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-[#0B1E3F] mt-2">{heading}</h1>
        <p className="text-neutral-600 mt-2 max-w-2xl">
          {subheading || "Powered by Gemini Nano Banana. Describe what you want, optionally upload reference photos, and we'll produce a print-ready banner concept."}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        <form onSubmit={generate} className="lg:col-span-2 space-y-5">
          <div className="pav-card">
            <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Describe your banner</label>
            <textarea
              data-testid="ai-prompt-input"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g. Birthday tarpaulin for 'Junior turns 7', superhero theme, blue and red, with photo placement on the right"
              className="pav-input mt-1.5 min-h-[120px]"
              required
            />

            <div className="grid grid-cols-3 gap-3 mt-4">
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Width (ft)</label>
                <input data-testid="ai-width-input" type="number" min="0" step="0.5"
                  value={width} onChange={(e) => setWidth(parseFloat(e.target.value) || 0)}
                  className="pav-input mt-1.5" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Height (ft)</label>
                <input data-testid="ai-height-input" type="number" min="0" step="0.5"
                  value={height} onChange={(e) => setHeight(parseFloat(e.target.value) || 0)}
                  className="pav-input mt-1.5" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Style</label>
                <select data-testid="ai-style-select" value={style} onChange={(e) => setStyle(e.target.value)}
                  className="pav-input mt-1.5">
                  {STYLE_PRESETS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
            </div>

            {/* Reference photos (up to 5) */}
            <div className="mt-5">
              <p className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Reference photos ({refs.length}/{MAX_REFS})</p>
              <p className="text-[11px] text-neutral-500 mt-0.5">Name each photo so it can be referenced in your description.</p>
              <div className="mt-2 space-y-2">
                {refs.map((r, idx) => (
                  <div key={idx} data-testid={`ai-ref-row-${idx}`}
                    className="flex items-center gap-2 p-2 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC]">
                    <img src={URL.createObjectURL(r.file)} alt="" className="h-10 w-10 rounded object-cover" />
                    <input className="pav-input flex-1 h-9 text-xs"
                      data-testid={`ai-ref-name-${idx}`}
                      placeholder={`Photo name (e.g. "logo", "subject portrait")`}
                      value={r.name} onChange={(e) => updateName(idx, e.target.value)} />
                    <button type="button" onClick={() => removeRef(idx)}
                      data-testid={`ai-ref-remove-${idx}`}
                      className="p-1 rounded-full hover:bg-red-50 text-red-500">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                {refs.length < MAX_REFS && (
                  <div className="dropzone cursor-pointer" onClick={() => document.getElementById("ai-ref-pick").click()}
                    data-testid="ai-ref-add">
                    <ImagePlus className="mx-auto h-7 w-7 text-[#0B1E3F]" />
                    <p className="text-sm mt-2">Add a reference photo (up to {MAX_REFS} total, max 20MB each)</p>
                  </div>
                )}
                <input id="ai-ref-pick" type="file" accept="image/*" className="hidden"
                  onChange={(e) => { addFile(e.target.files?.[0]); e.target.value = ""; }} />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="ai-generate-button"
              className="mt-5 w-full btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {loading ? "Generating…" : "Generate design"}
            </button>
          </div>
        </form>

        <div className="lg:col-span-3">
          <div className="pav-card min-h-[420px] flex flex-col items-center justify-center bg-gradient-to-br from-[#F4F6FB] to-[#F8FAFC]" data-testid="ai-result-pane">
            {loading ? (
              <div className="text-center">
                <Loader2 className="h-12 w-12 mx-auto animate-spin text-[#0B1E3F]" />
                <p className="mt-4 font-display font-bold text-lg text-[#0B1E3F]">Crafting your banner…</p>
                <p className="text-sm text-neutral-500 mt-1">This usually takes 10–25 seconds.</p>
              </div>
            ) : unavailable ? (
              <div className="text-center max-w-md" data-testid="ai-unavailable-card">
                <div className="h-14 w-14 mx-auto rounded-full bg-amber-50 border-2 border-amber-200 flex items-center justify-center">
                  <Sparkles className="h-7 w-7 text-amber-600" />
                </div>
                <p className="mt-4 font-display font-bold text-lg text-[#0B1E3F]">AI service temporarily unavailable</p>
                <p className="text-sm text-neutral-600 mt-2">
                  We&apos;re experiencing high demand on the AI image service. Please try again in a few minutes —
                  your prompt is preserved, just hit <span className="font-semibold">Generate</span> again.
                </p>
                <button onClick={() => setUnavailable(false)}
                  className="btn-outline text-sm inline-flex items-center gap-2 mt-4"
                  data-testid="ai-unavailable-retry">
                  Try again
                </button>
              </div>
            ) : result ? (
              <div className="w-full">
                {lrId && (
                  <div className="mb-3 px-3 py-2 rounded-lg bg-[#06B6D4]/10 border border-[#06B6D4]/30 text-xs text-[#0B1E3F] flex items-center gap-2" data-testid="ai-lr-context-banner">
                    <Sparkles className="h-3.5 w-3.5 text-[#06B6D4]" />
                    <span>Linked to Layout Request <span className="font-mono font-semibold">{lrId}</span> — you can attach this design directly for customer approval.</span>
                  </div>
                )}
                <img src={fileUrl(result.ai_design_url)} alt="Generated tarpaulin" className="w-full rounded-xl shadow-md" data-testid="ai-result-image" />
                <div className="mt-4 flex flex-wrap gap-3">
                  <a href={fileUrl(result.ai_design_url)} target="_blank" rel="noreferrer"
                    className="btn-outline inline-flex items-center gap-2 text-sm">
                    <Download className="h-4 w-4" /> Download
                  </a>
                  {lrId && (
                    <button
                      type="button"
                      onClick={attachToLayoutRequest}
                      disabled={attaching}
                      data-testid="ai-attach-to-lr-button"
                      className="btn-primary inline-flex items-center gap-2 text-sm disabled:opacity-50"
                    >
                      {attaching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      {attaching ? "Sending…" : "Save to Layout Request"}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center max-w-md">
                <Sparkles className="h-12 w-12 mx-auto text-[#06B6D4]" />
                <p className="mt-4 font-display font-bold text-lg text-[#0B1E3F]">Your design appears here</p>
                <p className="text-sm text-neutral-500 mt-2">Describe what you want — birthdays, store grand-openings, political campaigns, events, weddings — anything. The AI tailors the layout to your dimensions.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const AIDesigner = () => <AIDesignerBody />;

export { AIDesignerBody };
export default AIDesigner;
